import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Paper,
  Grid,
  Chip,
  IconButton,
  Button,
  Divider,
  Select,
  MenuItem,
  FormControl,
} from "@mui/material";

import {
  TrendingUp as SalesIcon,
  Business as BusinessIcon,
  Store as StoreIcon,
  People as PeopleIcon,
  Inventory as InventoryIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

import { useAuth } from '../../hooks/useAuth.js';

const SuperAdminDashboard = ({ onNotification }) => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Super Admin specific data
  const adminStats = {
    totalVendors: {
      count: 24,
      growth: "+12%",
      icon: BusinessIcon,
      color: "#1976d2",
    },
    totalBranches: {
      count: 156,
      growth: "+8%",
      icon: StoreIcon,
      color: "#388e3c",
    },
    totalRevenue: {
      amount: 2450000,
      growth: "+15%",
      icon: SalesIcon,
      color: "#f57c00",
    },
    activeUsers: {
      count: 892,
      growth: "+5%",
      icon: PeopleIcon,
      color: "#7b1fa2",
    },
  };

  // Vendor performance data
  const vendorPerformanceData = [
    { vendor: "Hotel Paradise", revenue: 450000, branches: 8, orders: 1250 },
    { vendor: "Royal Restaurant", revenue: 380000, branches: 6, orders: 980 },
    { vendor: "Cafe Central", revenue: 320000, branches: 4, orders: 850 },
    { vendor: "Food Plaza", revenue: 280000, branches: 5, orders: 720 },
    { vendor: "Dine & Wine", revenue: 250000, branches: 3, orders: 650 },
  ];

  // Monthly revenue trend
  const monthlyRevenueData = [
    { month: "Jan", revenue: 1800000 },
    { month: "Feb", revenue: 1950000 },
    { month: "Mar", revenue: 2100000 },
    { month: "Apr", revenue: 2250000 },
    { month: "May", revenue: 2400000 },
    { month: "Jun", revenue: 2450000 },
  ];

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [chartType, setChartType] = useState("bar");
  const [loading, setLoading] = useState(false);

  const formatDate = (date) => {
    const day = date.getDate();
    const monthName = date.toLocaleDateString("en-US", { month: "short" });
    return `${day}${getSuffix(day)} ${monthName}`;
  };

  const getSuffix = (day) => {
    if (day > 3 && day < 21) return "th";
    switch (day % 10) {
      case 1: return "st";
      case 2: return "nd";
      case 3: return "rd";
      default: return "th";
    }
  };

  const handleRefresh = () => {
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
    }, 1000);
  };

  const StatCard = ({ title, value, growth, icon: Icon, color }) => (
    <Card
      sx={{
        height: "100%",
        minHeight: 140,
        borderRadius: 3,
        boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        cursor: "pointer",
        "&:hover": {
          transform: "translateY(-6px)",
          boxShadow: "0 12px 24px rgba(0,0,0,0.15)",
        },
        background: `linear-gradient(135deg, ${color}08 0%, ${color}05 100%)`,
        border: `1px solid ${color}20`,
      }}
    >
      <CardContent sx={{ p: 3, "&:last-child": { pb: 3 } }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mb: 1 }}>
              {title}
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, color, mb: 1, lineHeight: 1.2 }}>
              {typeof value === 'number' && value > 1000 ? value.toLocaleString() : value}
            </Typography>
            <Chip
              label={growth}
              color="success"
              size="small"
              sx={{
                fontWeight: 600,
                fontSize: '0.75rem',
                height: 24,
                backgroundColor: '#e8f5e8',
                color: '#2e7d32',
              }}
            />
          </Box>
          <Box
            sx={{
              backgroundColor: `${color}15`,
              p: 1.5,
              borderRadius: 2.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              ml: 2,
            }}
          >
            <Icon sx={{ color, fontSize: 28 }} />
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box 
      sx={{ 
        p: { xs: 1.5, sm: 2, md: 3 }, 
        maxWidth: '100%', 
        width: '100%',
        flex: 1,
        overflowY: 'auto',
        overflowX: 'hidden',
        }}>
        {/* HEADER */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 4,
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 0.5 }}>
              Super Admin Dashboard
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Welcome back! Here's your business overview
            </Typography>
          </Box>
        </Box>

        {/* SUMMARY CARDS */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              lg: "repeat(4, 1fr)"
            },
            gap: { xs: 1.5, sm: 2, md: 2.5 },
            mb: 4,
          }}
        >
          <StatCard
            title="Total Vendors"
            value={adminStats.totalVendors.count}
            growth={adminStats.totalVendors.growth}
            icon={BusinessIcon}
            color={adminStats.totalVendors.color}
          />
          <StatCard
            title="Total Branches"
            value={adminStats.totalBranches.count}
            growth={adminStats.totalBranches.growth}
            icon={StoreIcon}
            color={adminStats.totalBranches.color}
          />
          <StatCard
            title="Total Revenue"
            value={`₹${(adminStats.totalRevenue.amount / 100000).toFixed(1)}L`}
            growth={adminStats.totalRevenue.growth}
            icon={SalesIcon}
            color={adminStats.totalRevenue.color}
          />
          <StatCard
            title="Active Users"
            value={adminStats.activeUsers.count}
            growth={adminStats.activeUsers.growth}
            icon={PeopleIcon}
            color={adminStats.activeUsers.color}
          />
        </Box>

        {/* REVENUE CHART & TOP VENDORS */}
        <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }}>
          {/* REVENUE TREND CHART */}
          <Grid item xs={12} lg={8}>
            <Paper sx={{ p: { xs: 2, sm: 2.5, md: 3 }, borderRadius: 2, boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  mb: 3,
                  gap: 2,
                  flexWrap: "wrap",
                }}
              >
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                    Revenue Trend
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Monthly revenue performance
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
                  <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 150 } }}>
                    <Select value={chartType} onChange={(e) => setChartType(e.target.value)}>
                      <MenuItem value="bar">Bar Chart</MenuItem>
                      <MenuItem value="area">Area Chart</MenuItem>
                      <MenuItem value="line">Line Chart</MenuItem>
                    </Select>
                  </FormControl>

                  <IconButton
                    onClick={handleRefresh}
                    color="primary"
                    disabled={loading}
                    title="Refresh data"
                    size="small"
                  >
                    <RefreshIcon />
                  </IconButton>
                </Box>
              </Box>

              <Box
                sx={{
                  width: "100%",
                  height: { xs: 250, sm: 300, md: 350 },
                  "& .recharts-cartesian-grid line": {
                    stroke: "#e0e0e0",
                  },
                  "& .recharts-cartesian-axis-line": {
                    stroke: "#9e9e9e",
                  },
                  "& .recharts-cartesian-axis-tick-value": {
                    fill: "#666",
                    fontSize: 12,
                  },
                }}
              >
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={monthlyRevenueData}
                    margin={{
                      top: 20,
                      right: 30,
                      left: 20,
                      bottom: 20,
                    }}
                  >
                    <defs>
                      <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#1976d2" stopOpacity={0.8}/>
                        <stop offset="95%" stopColor="#1976d2" stopOpacity={0.3}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f5" />
                    <XAxis
                      dataKey="month"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 12, fill: '#666' }}
                    />
                    <YAxis
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 12, fill: '#666' }}
                      tickFormatter={(value) => `₹${(value / 100000).toFixed(0)}L`}
                    />
                    <Tooltip
                      formatter={(value) => [`₹${value.toLocaleString()}`, "Revenue"]}
                      labelStyle={{
                        color: '#333',
                        fontWeight: 600,
                      }}
                      contentStyle={{
                        backgroundColor: '#fff',
                        border: '1px solid #e0e0e0',
                        borderRadius: 8,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      }}
                      cursor={{ fill: 'rgba(25, 118, 210, 0.1)' }}
                    />
                    <Legend
                      wrapperStyle={{
                        paddingTop: '20px',
                        fontSize: '14px',
                      }}
                    />
                    <Bar
                      dataKey="revenue"
                      fill="url(#revenueGradient)"
                      name="Revenue"
                      radius={[6, 6, 0, 0]}
                      animationDuration={1500}
                      animationEasing="ease-out"
                    />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Paper>
          </Grid>

          {/* TOP PERFORMING VENDORS */}
          <Grid item xs={12} lg={4}>
            <Paper sx={{ p: { xs: 2, sm: 2.5, md: 3 }, borderRadius: 2, boxShadow: "0 1px 3px rgba(0,0,0,0.05)", height: "100%" }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2.5 }}>
                Top Vendors
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {vendorPerformanceData.map((vendor, index) => (
                <Box
                  key={vendor.vendor}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    py: 2,
                    gap: 1.5,
                    borderBottom: index < vendorPerformanceData.length - 1 ? "1px solid #f0f0f0" : "none",
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {vendor.vendor}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {vendor.branches} branches • {vendor.orders} orders
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: "#1976d2", flexShrink: 0 }}>
                    ₹{vendor.revenue.toLocaleString()}
                  </Typography>
                </Box>
              ))}

              <Button
                fullWidth
                variant="outlined"
                sx={{ mt: 3 }}
                onClick={() => navigate('/vendors')}
              >
                View All Vendors
              </Button>
            </Paper>
          </Grid>
        </Grid>

        {/* QUICK ACTIONS */}
        <Paper sx={{ p: { xs: 2, sm: 2.5, md: 3 }, borderRadius: 2, boxShadow: "0 1px 3px rgba(0,0,0,0.05)", mt: 4 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
            Quick Actions
          </Typography>
          <Grid container spacing={{ xs: 1.5, sm: 2 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<BusinessIcon />}
                onClick={() => navigate('/vendors/create')}
                sx={{ py: 2.5, textTransform: 'none' }}
              >
                Add New Vendor
              </Button>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<StoreIcon />}
                onClick={() => navigate('/branches')}
                sx={{ py: 2.5, textTransform: 'none' }}
              >
                Manage Branches
              </Button>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<PeopleIcon />}
                onClick={() => navigate('/staff')}
                sx={{ py: 2.5, textTransform: 'none' }}
              >
                Staff Management
              </Button>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<InventoryIcon />}
                onClick={() => navigate('/products')}
                sx={{ py: 2.5, textTransform: 'none' }}
              >
                Product Catalog
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Box>
  );
};

export { SuperAdminDashboard };